// frontend/src/pages/PurchaseOrderForm.js

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL'; // adjust the path as needed!
import SearchableSelect from '../components/SearchableSelect';
import {
  Panel, Button, Field, ErrorNote, Note, inputClass, textareaClass,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

const cell = `${inputClass} h-[30px] px-2 text-[12px] rounded-[7px]`;

const PurchaseOrderForm = ({ users = [], onSubmitted }) => {
  const { token } = useAuth();

  const [recipientId, setRecipientId] = useState('');
  const [recipientRole, setRecipientRole] = useState('');
  const [recipientBusinessName, setRecipientBusinessName] = useState('');
  const [items, setItems] = useState([
    { description: '', quantity: 1, price: 0 }
  ]);
  // The car the parts are for. A bon de commande had no vehicle on it at all,
  // so the supplier receiving one had to guess which variant of a part was
  // wanted. Optional, because a stock order is not for a particular car.
  const [vin, setVin] = useState('');
  const [note, setNote] = useState('');
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // When recipient changes, update recipient info
  const handleRecipientChange = (e) => {
    const id = e.target.value;
    setRecipientId(id);
    const found = users.find(u => u._id === id);
    setRecipientRole(found?.role || '');
    setRecipientBusinessName(found?.businessName || found?.name || '');
  };

  const handleItemChange = (idx, field, value) => {
    setItems(prev =>
      prev.map((item, i) =>
        i === idx
          ? { ...item, [field]: field === 'quantity' || field === 'price' ? Number(value) : value }
          : item
      )
    );
  };

  const handleAddItem = () => setItems(prev => [...prev, { description: '', quantity: 1, price: 0 }]);
  const handleRemoveItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // Calculate total
  React.useEffect(() => {
    setTotal(items.reduce((sum, item) => sum + item.quantity * item.price, 0));
  }, [items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOkMsg('');
    try {
      const payload = {
        recipient: {
          id: recipientId,
          role: recipientRole,
          businessName: recipientBusinessName,
        },
        items,
        vin: vin.trim().toUpperCase(),
        note,
        total,
      };
      const res = await fetch(`${API_URL}/purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || 'Échec de la création du bon de commande');
      }
      setOkMsg('Bon de commande envoyé.');
      setItems([{ description: '', quantity: 1, price: 0 }]);
      setRecipientId('');
      setRecipientRole('');
      setRecipientBusinessName('');
      setVin('');
      setNote('');
      setTotal(0);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <Panel title="Nouveau bon de commande">
      <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
        {error && <ErrorNote>{error}</ErrorNote>}
        {okMsg && <Note tone="ok">{okMsg}</Note>}

        <Field label="Fournisseur" required>
          <SearchableSelect
            options={users.map(u => ({
              value: u._id,
              label: `${u.businessName || u.name || u.email}`,
            }))}
            value={recipientId}
            onChange={(val) => handleRecipientChange({ target: { value: val } })}
            placeholder="Sélectionner un fournisseur"
            required
          />
        </Field>

        <Field
          label="VIN du véhicule"
          hint="Facultatif — laissez vide pour une commande de stock"
        >
          <input
            className={`${inputClass} tracking-[.08em] uppercase`}
            value={vin}
            onChange={(e) => setVin(e.target.value)}
            placeholder="17 caractères, copiés depuis le devis"
            maxLength={17}
          />
        </Field>

        {/* Items */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-2.5">
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Lignes</p>
            <Button as="button" type="button" className="h-[28px] px-2.5 text-[11.5px]" onClick={handleAddItem}>
              + Ligne
            </Button>
          </div>

          <div className="rounded-xl border border-[#241F1B] overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: 560 }}>
              <thead>
                <tr className="border-b border-[#241F1B]">
                  {['Désignation', 'Qté', 'P.U.', 'Total', ''].map((h, i) => (
                    <th key={i}
                        className="px-2.5 py-2.5 text-left text-[10px] uppercase tracking-[.14em] text-[#6E665C] font-normal whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b border-[#1E1B18] last:border-0">
                    <td className="px-2.5 py-2">
                      <input
                        className={cell}
                        value={item.description}
                        onChange={e => handleItemChange(idx, 'description', e.target.value)}
                        required
                        placeholder="Désignation de la pièce…"
                      />
                    </td>
                    <td className="px-2.5 py-2 w-[80px]">
                      <input
                        className={`${cell} text-center`}
                        type="number"
                        value={item.quantity}
                        min={1}
                        onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                        required
                      />
                    </td>
                    <td className="px-2.5 py-2 w-[120px]">
                      <input
                        className={`${cell} text-right`}
                        type="number"
                        min={0}
                        step={0.01}
                        value={item.price}
                        onChange={e => handleItemChange(idx, 'price', e.target.value)}
                        required
                      />
                    </td>
                    <td className="px-2.5 py-2 w-[120px] text-right whitespace-nowrap">
                      <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[12.5px] text-[#F4F1EA]">
                        {fmt(item.quantity * item.price)}
                      </span>
                    </td>
                    <td className="px-2.5 py-2 w-[36px] text-right">
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(idx)}
                          className="text-[#8F877B] hover:text-[#E2574B] transition-colors p-1"
                          aria-label="Retirer la ligne"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                               strokeWidth="1.8" strokeLinecap="round">
                            <path d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Field label="Note">
          <textarea
            className={textareaClass}
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="Délai souhaité, conditions de livraison…"
          />
        </Field>

        <div className="flex items-center justify-between gap-4 pt-3.5 border-t border-[#241F1B]">
          <span className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Total</span>
          <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[19px] font-semibold text-[#F4F1EA]">
            {fmt(total)} <span className="text-[12px] text-[#6E665C]">F</span>
          </span>
        </div>

        <div>
          <Button as="button" type="submit" variant="primary" disabled={loading} className="w-full sm:w-auto">
            {loading ? 'Envoi…' : 'Envoyer le bon de commande'}
          </Button>
        </div>
      </form>
    </Panel>
  );
};

export default PurchaseOrderForm;
